/**
 * Content Editor Module
 * 
 * Handles content editing operations for the human-in-the-loop scanning workflow.
 * Used by scanning agents and admins to review, edit, and publish scanned books.
 */

import { supabase } from '@/lib/supabase/client';
import type { ScannedBookPage, LayoutData, RenderingHints } from './types';
import { addBookToLibrary, getRequestingParents, resolveMatchingScanRequests } from './book-management';

// ============================================================================
// Types
// ============================================================================

/**
 * Digital page content as defined by the content editor.
 * This is the human-defined structure that replaces auto-sentence-splitting.
 */
export interface DigitalPageContent {
  /** Source page ID this content comes from */
  sourcePageId: string;
  /** The text content for this digital page */
  text: string;
  /** Digital page number (1-based, global order in book) */
  digitalPageNumber: number;
}

/**
 * Page edit data for the content editor
 */
export interface PageEditData {
  id: string;
  pageNumber: number;
  extractedText: string;
  pageType: string;
  layoutData: LayoutData | null;
  renderingHints: RenderingHints | null;
  imageUrl: string | null;
  enhancedImageUrl: string | null;
  needsRescan: boolean;
  processingStatus: string;
  isTwoPageSpread: boolean;
}

/**
 * Book content for the editor
 */
export interface BookContentForEditor {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  binding: string | null;
  contentSource: string | null;
  qualityStatus: string;
  qualityIssues: string[] | null;
  reconstructedText: string | null;
  digitalPageContent: DigitalPageContent[] | null;
  pages: PageEditData[];
}

// ============================================================================
// Load Book Content for Editor
// ============================================================================

/**
 * Load all book content needed for the content editor
 */
export async function loadBookForEditor(bookId: string): Promise<BookContentForEditor | null> {
  // Fetch book
  const { data: book, error: bookError } = await supabase
    .from('scanned_books')
    .select('id, title, author, isbn, binding, content_source, quality_status, quality_issues, reconstructed_text, digital_page_content')
    .eq('id', bookId)
    .maybeSingle();

  if (bookError || !book) {
    console.error('Error loading book:', bookError);
    return null;
  }

  // Fetch pages
  const { data: pages, error: pagesError } = await supabase
    .from('scanned_book_pages')
    .select('id, page_number, extracted_text, page_type, layout_data, rendering_hints, image_url, enhanced_image_url, needs_rescan, processing_status, is_two_page_spread')
    .eq('book_id', bookId)
    .eq('is_superseded', false)
    .order('page_number', { ascending: true });

  if (pagesError) {
    console.error('Error loading pages:', pagesError);
    return null;
  }

  return {
    id: book.id,
    title: book.title,
    author: book.author,
    isbn: book.isbn,
    binding: book.binding || null,
    contentSource: book.content_source || null,
    qualityStatus: book.quality_status || 'processing',
    qualityIssues: book.quality_issues,
    reconstructedText: book.reconstructed_text,
    digitalPageContent: (book.digital_page_content as DigitalPageContent[] | null) || null,
     
    pages: (pages || []).map((p: any) => ({
      id: p.id,
      pageNumber: p.page_number,
      extractedText: p.extracted_text || '',
      pageType: p.page_type || 'text',
      layoutData: p.layout_data,
      renderingHints: p.rendering_hints,
      imageUrl: p.image_url,
      enhancedImageUrl: p.enhanced_image_url,
      needsRescan: p.needs_rescan || false,
      processingStatus: p.processing_status || 'pending',
      isTwoPageSpread: p.is_two_page_spread || false,
    })),
  };
}

// ============================================================================
// Book Metadata Editing
// ============================================================================

export async function updateBookBinding(bookId: string, binding: string | null): Promise<void> {
  const { error } = await supabase
    .from('scanned_books')
    .update({ binding })
    .eq('id', bookId);

  if (error) throw new Error(`Failed to update binding: ${error.message}`);
}

// ============================================================================
// Page Editing
// ============================================================================

/**
 * Update a single page's content
 */
export async function updatePageContent(
  pageId: string,
  updates: {
    extractedText?: string;
    pageType?: string;
    layoutData?: LayoutData | null;
    renderingHints?: RenderingHints | null;
    needsRescan?: boolean;
  }
): Promise<void> {
  const dbUpdates: Record<string, unknown> = {};
  
  if (updates.extractedText !== undefined) {
    dbUpdates.extracted_text = updates.extractedText;
  }
  if (updates.pageType !== undefined) {
    dbUpdates.page_type = updates.pageType;
  }
  if (updates.layoutData !== undefined) {
    dbUpdates.layout_data = updates.layoutData;
  }
  if (updates.renderingHints !== undefined) {
    dbUpdates.rendering_hints = updates.renderingHints;
  }
  if (updates.needsRescan !== undefined) {
    dbUpdates.needs_rescan = updates.needsRescan;
  }

  const { error } = await supabase
    .from('scanned_book_pages')
    .update(dbUpdates)
    .eq('id', pageId);

  if (error) {
    throw new Error(`Failed to update page: ${error.message}`);
  }
}

/**
 * Merge two pages into one (combine text, keep first page's image)
 */
export async function mergePages(
  keepPageId: string,
  removePageId: string
): Promise<void> {
  // Get both pages
  const { data: pages, error: fetchError } = await supabase
    .from('scanned_book_pages')
    .select('id, extracted_text')
    .in('id', [keepPageId, removePageId]);

  if (fetchError || !pages || pages.length !== 2) {
    throw new Error('Failed to fetch pages for merge');
  }

  const keepPage = pages.find(p => p.id === keepPageId);
  const removePage = pages.find(p => p.id === removePageId);

  if (!keepPage || !removePage) {
    throw new Error('Pages not found');
  }

  // Combine text
  const combinedText = [keepPage.extracted_text, removePage.extracted_text]
    .filter(Boolean)
    .join('\n\n');

  // Update keep page
  await updatePageContent(keepPageId, { extractedText: combinedText });

  // Mark remove page as superseded
  const { error: supersedError } = await supabase
    .from('scanned_book_pages')
    .update({ is_superseded: true })
    .eq('id', removePageId);

  if (supersedError) {
    throw new Error(`Failed to supersede page: ${supersedError.message}`);
  }
}

/**
 * Split a page into two pages at a specific position
 */
export async function splitPage(
  pageId: string,
  splitPosition: number
): Promise<string> {
  // Get the page
  const { data: page, error: fetchError } = await supabase
    .from('scanned_book_pages')
    .select('id, book_id, page_number, extracted_text, page_type, image_url')
    .eq('id', pageId)
    .maybeSingle();

  if (fetchError || !page) {
    throw new Error('Failed to fetch page for split');
  }

  const text = page.extracted_text || '';
  const firstPart = text.slice(0, splitPosition).trim();
  const secondPart = text.slice(splitPosition).trim();

  // Update original page with first part
  await updatePageContent(pageId, { extractedText: firstPart });

  // Create new page with second part
  const { data: newPage, error: insertError } = await supabase
    .from('scanned_book_pages')
    .insert({
      book_id: page.book_id,
      page_number: page.page_number + 0.5, // Will be renumbered later
      extracted_text: secondPart,
      page_type: page.page_type,
      image_url: page.image_url, // Share the same image
      processing_status: 'complete',
    })
    .select()
    .single();

  if (insertError || !newPage) {
    throw new Error(`Failed to create split page: ${insertError?.message}`);
  }

  return newPage.id;
}

/**
 * Reorder pages in a book
 */
export async function reorderPages(
  bookId: string,
  pageOrder: string[]
): Promise<void> {
  // Update each page with its new order
  for (let i = 0; i < pageOrder.length; i++) {
    const { error } = await supabase
      .from('scanned_book_pages')
      .update({ page_number: i + 1 })
      .eq('id', pageOrder[i]);

    if (error) {
      throw new Error(`Failed to reorder page: ${error.message}`);
    }
  }
}

// ============================================================================
// Digital Page Content (Human-Defined Breaks)
// ============================================================================

/**
 * Save digital page content to the book.
 * This is the human-defined structure that replaces auto-sentence-splitting.
 */
export async function saveDigitalPageContent(
  bookId: string,
  content: DigitalPageContent[]
): Promise<void> {
  const { error } = await supabase
    .from('scanned_books')
     
    .update({ digital_page_content: content as any })
    .eq('id', bookId);

  if (error) {
    throw new Error(`Failed to save digital page content: ${error.message}`);
  }

  console.log(`[ContentEditor] Saved ${content.length} digital page definitions for book ${bookId}`);
}

/**
 * Generate digital page content from text with markers.
 * Markers are represented as "---PAGE_BREAK---" in the text.
 */
export function parseDigitalPageBreaks(
  fullText: string,
  sourcePageId: string
): DigitalPageContent[] {
  const PAGE_BREAK_MARKER = '---PAGE_BREAK---';
  const segments = fullText.split(PAGE_BREAK_MARKER);
  
  return segments
    .map((text, index) => text.trim())
    .filter(text => text.length > 0)
    .map((text, index) => ({
      sourcePageId,
      text,
      digitalPageNumber: index + 1,
    }));
}

/**
 * Convert full book text with page break markers to digital page content.
 * Each segment between markers becomes one digital page.
 */
export function generateDigitalPageContentFromMarkedText(
  markedText: string,
  pages: PageEditData[]
): DigitalPageContent[] {
  const PAGE_BREAK_MARKER = '---PAGE_BREAK---';
  const segments = markedText.split(PAGE_BREAK_MARKER);
  
  // For now, assign all segments to the first story page
  // In a more sophisticated implementation, we'd track which source page each segment came from
  const storyPage = pages.find(p => p.pageType === 'story' || p.pageType === 'text');
  const sourcePageId = storyPage?.id || pages[0]?.id || '';
  
  return segments
    .map(text => text.trim())
    .filter(text => text.length > 0)
    .map((text, index) => ({
      sourcePageId,
      text,
      digitalPageNumber: index + 1,
    }));
}

// ============================================================================
// Status Helpers
// ============================================================================

type QualityStatus = 'processing' | 'reconstructing' | 'turn_codes_pending' | 'needs_review' | 'failed' | 'ready' | null;

/**
 * Whether a save operation should preserve the current quality_status rather
 * than regressing it to 'needs_review'. Once a book reaches turn_codes_pending
 * or ready, an explicit admin action (not an edit-save) is needed to change it.
 */
export function shouldPreserveStatus(currentStatus: string | null): boolean {
  return currentStatus === 'turn_codes_pending' || currentStatus === 'ready';
}

// ============================================================================
// Publishing
// ============================================================================

/**
 * Publish a book after content editing.
 * 
 * Sets quality_status to 'turn_codes_pending' so an admin can review
 * turn color codes before the book becomes available to parents.
 * Digital pages are generated so the admin can preview during review.
 */
export async function publishBook(
  bookId: string,
  digitalPageContent: DigitalPageContent[]
): Promise<{ success: boolean }> {
  // Save digital page content
  await saveDigitalPageContent(bookId, digitalPageContent);

  // Generate digital pages so admin can preview during turn code review
  await generateDigitalPagesFromContent(bookId, digitalPageContent);

  // Transition to turn_codes_pending — admin must approve turn codes
  const { error: updateError } = await supabase
    .from('scanned_books')
    .update({
      quality_status: 'turn_codes_pending',
      scan_completed: true,
    })
    .eq('id', bookId);

  if (updateError) {
    throw new Error(`Failed to update book status: ${updateError.message}`);
  }

  // Resolve matching scan requests so the book drops from agent's "My Active Work"
  const { data: bookData } = await supabase
    .from('scanned_books')
    .select('isbn')
    .eq('id', bookId)
    .maybeSingle();

  if (bookData?.isbn) {
    await resolveMatchingScanRequests(bookId, bookData.isbn);
  }

  console.log(`[ContentEditor] Published book ${bookId} → turn_codes_pending`);

  return { success: true };
}

/**
 * Approve a book's turn codes and make it available to parents.
 * 
 * Called by admin after reviewing turn color codes. This:
 * 1. Sets quality_status to 'ready'
 * 2. Adds book to all requesting parents' libraries
 * 3. Resolves matching scan requests
 * 4. Sends "book ready" email notifications
 */
export async function approveBookTurnCodes(
  bookId: string
): Promise<{ success: boolean; parentsNotified: number }> {
  const { data: book, error: bookError } = await supabase
    .from('scanned_books')
    .select('isbn, title')
    .eq('id', bookId)
    .maybeSingle();

  if (bookError || !book) {
    throw new Error('Failed to fetch book for approval');
  }

  const { error: updateError } = await supabase
    .from('scanned_books')
    .update({ quality_status: 'ready' })
    .eq('id', bookId);

  if (updateError) {
    throw new Error(`Failed to update book status: ${updateError.message}`);
  }

  const requestingParents = await getRequestingParents(book.isbn || undefined);

  for (const parentId of requestingParents) {
    try {
      await addBookToLibrary(parentId, bookId);
    } catch (err) {
      console.warn(`Failed to add book to parent ${parentId} library:`, err);
    }
  }

  await resolveMatchingScanRequests(bookId, book.isbn || undefined);

  notifyBookReady(bookId).catch((err) =>
    console.error('[ContentEditor] Failed to send book-ready notification:', err)
  );

  console.log(`[ContentEditor] Approved book ${bookId}, notified ${requestingParents.length} parents`);

  return {
    success: true,
    parentsNotified: requestingParents.length,
  };
}

/**
 * Send "book ready" email via the book-notifications edge function.
 * Non-blocking — caller uses fire-and-forget.
 */
async function notifyBookReady(bookId: string): Promise<void> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return;

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-notifications`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.session.access_token}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ action: 'send-book-ready', bookId }),
    }
  );
}

/**
 * Generate digital_pages table entries from human-defined content.
 */
async function generateDigitalPagesFromContent(
  bookId: string,
  content: DigitalPageContent[]
): Promise<void> {
  // Clear existing digital pages
  const { error: deleteError } = await supabase
    .from('digital_pages')
    .delete()
    .eq('book_id', bookId);

  if (deleteError) {
    throw new Error(`Failed to clear existing digital pages: ${deleteError.message}`);
  }

  if (content.length === 0) {
    return;
  }

  // Insert new digital pages with per-source-page sentence_index
  const sourcePageCounters = new Map<string, number>();
  const pages = content.map((item, index) => {
    const counter = sourcePageCounters.get(item.sourcePageId) ?? 0;
    sourcePageCounters.set(item.sourcePageId, counter + 1);
    return {
      book_id: bookId,
      source_page_id: item.sourcePageId,
      digital_page_number: item.digitalPageNumber || index + 1,
      sentence_text: item.text,
      sentence_index: counter,
    };
  });

  const { error: insertError } = await supabase
    .from('digital_pages')
    .insert(pages);

  if (insertError) {
    throw new Error(`Failed to generate digital pages: ${insertError.message}`);
  }

  console.log(`[ContentEditor] Generated ${pages.length} digital pages for book ${bookId}`);
}

// ============================================================================
// Reconstructed Text Management
// ============================================================================

/**
 * Save reconstructed text for a book.
 * This is the clean, complete story text used for reading.
 */
export async function saveReconstructedText(
  bookId: string,
  text: string
): Promise<void> {
  const { error } = await supabase
    .from('scanned_books')
    .update({ reconstructed_text: text })
    .eq('id', bookId);

  if (error) {
    throw new Error(`Failed to save reconstructed text: ${error.message}`);
  }
}

/**
 * Generate reconstructed text from all story pages.
 */
export async function generateReconstructedText(bookId: string): Promise<string> {
  const { data: pages, error } = await supabase
    .from('scanned_book_pages')
    .select('extracted_text, page_type')
    .eq('book_id', bookId)
    .eq('is_superseded', false)
    .in('page_type', ['story', 'text', 'chapter'])
    .order('page_number', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch pages: ${error.message}`);
  }

  const text = (pages || [])
    .map(p => p.extracted_text)
    .filter(Boolean)
    .join('\n\n');

  await saveReconstructedText(bookId, text);

  return text;
}
